import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Skeleton,
  Pagination,
  FormControl,
  Autocomplete,
  IconButton,
} from "@mui/material";
import styles, {
  appendTeamMemberLawyerId,
  debounce,
  pageScrollStyle,
} from "../../../../styles/styles";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import { useDialog } from "../../../DialogContext";
import CreatecaseTemplateForm from "./CreatecaseTemplateForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getCaseTemplate,
  getPracticeArea,
} from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { backend_api } from "../../../../static/server";
import { toast } from "react-toastify";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import noDataFound from "../../../../assets/no-data-found.webp";
import { Controller, useForm } from "react-hook-form";
import { useAccess } from "../../../Auth/AccessProvider";
import { isDevMode, useSubscription } from "../../../Auth/SubscriptionContext";
import {
  AddRounded,
  BorderColorRounded,
  CloseRounded,
  LockRounded,
  SearchRounded,
} from "@mui/icons-material";
import MatterForm from "../../Cases/MatterForm";
import FilterDrawer from "../../../FilterDrawer";

const TemplateCardSkeleton = () => (
  <Box
    sx={{
      borderRadius: "0.625rem",
      border: "1px solid #DADADA",
      overflow: "hidden",
    }}
  >
    <Box sx={{ p: "0.5rem", bgcolor: "#F5F5F5" }}>
      <Skeleton variant="text" width="70%" height={20} />
    </Box>
    <Box sx={{ p: "1rem" }}>
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="85%" />

      <Box sx={{ mt: "0.75rem" }}>
        <Skeleton variant="text" width="60%" height={19} />
      </Box>
      <Box sx={{ mt: "0.3rem" }}>
        <Skeleton variant="text" width="70%" height={19} />
      </Box>
      <Box sx={{ mt: "0.3rem" }}>
        <Skeleton variant="text" width="65%" height={19} />
      </Box>
    </Box>
    <Box
      sx={{
        borderTop: "1px solid #DADADA",
        height: "2rem",
        px: "0.5rem",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Skeleton variant="text" width="50%" height={24} />
    </Box>
  </Box>
);

const NoTemplateFound = () => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "1rem",
    }}
  >
    <Box sx={{ height: "15rem" }}>
      <img
        src={noDataFound}
        alt="No Data"
        style={{
          height: "100%",
          width: "100%",
          objectFit: "contain",
        }}
        onError={(e) => {
          e.onerror = null;
          e.target.src = require("../../../../assets/no-data-found.webp");
        }}
      />
    </Box>
    <Typography
      variant="body1"
      sx={{
        fontSize: "0.9rem",
        textAlign: "center",
      }}
    >
      No templates available. Please create a new template to get started.
    </Typography>
  </Box>
);

const CaseTemplate = () => {
  const dispatch = useDispatch();
  const { showDialog, closeDialog } = useDialog();
  const { isLimitReached } = useSubscription();
  const { hasPageAccess } = useAccess();
  const [searchCaseTemplate, setSearchCaseTemplate] = useState("");
  const [expandedDesc, setExpandedDesc] = useState({});
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const { allPracticeArea, allCaseTemplate, loading, error } = useSelector(
    (state) => state.case,
  );
  const { page, pageSize, count } = useSelector(
    (state) => state.case.caseTemplatePagination,
  );
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    priority: [],
  });
  const isFilterApplied = useMemo(() => {
    return activeFilters.priority.length > 0;
  }, [activeFilters]);
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    dispatch(
      getCaseTemplate({
        url: `${backend_api}case/template-register/`,
        token: token,
        teamMemberLawyerId,
      }),
    );
    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      }),
    );
  }, [dispatch, token, teamMemberLawyerId]);

  const duplicateTemplateData = async (template) => {
    try {
      const res = await axios.post(
        appendTeamMemberLawyerId(
          `${backend_api}case/template-register/${template?.id}/duplicate/`,
          teamMemberLawyerId,
        ),
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );
      return res?.data;
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || "Failed to duplicate matter template",
      );
      return null;
    } finally {
      dispatch(
        getCaseTemplate({
          url: `${backend_api}case/template-register/`,
          token: token,
          teamMemberLawyerId,
        }),
      );
    }
  };

  const handleEditTemplate = async (template) => {
    if (template?.is_standard) {
      const duplicate = await duplicateTemplateData(template);
      if (duplicate) {
        showDialog(<CreatecaseTemplateForm editTempData={duplicate} />, {
          dialogTitle: "Edit Matter Template",
          fullScreen: true,
          width: "100%",
          formType: "matter",
          dialogId: "create-matter-template",
        });
      }
    } else {
      showDialog(<CreatecaseTemplateForm editTempData={template} />, {
        dialogTitle: "Edit Matter Template",
        fullScreen: true,
        width: "100%",
        formType: "matter",
        dialogId: "create-matter-template",
      });
    }
  };

  const handlePageChange = (e, value) => {
    e.preventDefault();
    dispatch(
      getCaseTemplate({
        url: `${backend_api}case/template-register/`,
        token: token,
        teamMemberLawyerId,
        page: value,
        pageSize: pageSize,
      }),
    );
  };

  const onSubmit = (tempId) => async (data) => {
    try {
      const res = await axios.patch(
        `${backend_api}case/template-register/${tempId}/`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );
      if (res?.status === 200) {
        toast.success("Note added successfully");
      }
    } catch (err) {
      toast.error("Failed to add the note ");
    } finally {
      reset();
      closeDialog();
      dispatch(
        getCaseTemplate({
          url: `${backend_api}case/template-register/`,
          token: token,
          teamMemberLawyerId,
        }),
      );
    }
  };

  const debouncedFetchCasesTemplate = useCallback(
    debounce((query) => {
      const searchParam = query ? `name=${encodeURIComponent(query)}` : "";
      const url = query
        ? `${backend_api}case/template-register/?${searchParam}`
        : `${backend_api}case/template-register/`;

      dispatch(
        getCaseTemplate({
          url: url,
          token,
          teamMemberLawyerId,
        }),
      );
    }, 500),
    [dispatch, backend_api, token],
  );

  const handleCaseTemplateSearch = (e) => {
    const value = e.target.value;
    setSearchCaseTemplate(value);
    debouncedFetchCasesTemplate(value);
  };

  const handleAddNote = (template) => {
    setValue("template_name", template?.template_name);
    setValue("practice_area", template?.practice_area?.id);
    setValue("template_description", template?.template_description);
    showDialog(
      <Box>
        <form onSubmit={handleSubmit(onSubmit(template?.id))}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <Controller
                name="template_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Template Name"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="practice_area"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={allPracticeArea || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        allPracticeArea?.find(
                          (option) => option?.id === field?.value,
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue?.id : null)
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Practice Area"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                )}
              />
            </Box>
            <Controller
              name="template_description"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Description"
                  error={!!errors?.template_description}
                  helperText={
                    errors?.template_description ? "This field is required" : ""
                  }
                />
              )}
            />
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => closeDialog()}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              Save Note
            </LoadingButton>
          </Box>
        </form>
      </Box>,
      { dialogTitle: "Add note" },
    );
  };

  const handleEditDescription = (template) => {
    handleAddNote(template);
  };

  const handleApplyFilters = () => {
    // setPage(1);
    setFilterAnchor(null);
  };

  const handleResetFilters = () => {
    setActiveFilters({
      priority: [],
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "1rem",
        }}
      >
        <h3
          className={`${styles.title}`}
          style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}
        >
          Matter Templates
        </h3>
        <Box
          sx={{
            display: "flex",
            gap: { xs: "0.75rem", sm: "1rem" },
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search by template name"
            value={searchCaseTemplate}
            onChange={handleCaseTemplateSearch}
            sx={{
              minWidth: "240px",
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-root": {
                borderRadius: "32px",
                pr: 0.5,
                backgroundColor: "#f8f9fa",
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: 0.5,
                fontSize: "0.8rem",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchCaseTemplate ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCaseTemplateSearch({ target: { value: "" } })
                      }
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                      }}
                    >
                      <SearchRounded fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          <Box>
            <Tooltip title="Filter" placement="bottom" arrow>
              <IconButton
                id="filter-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterAnchor(e.currentTarget);
                }}
                sx={{
                  border: "1px solid",
                  borderColor: isFilterApplied ? "primary.main" : "divider",
                  bgcolor: isFilterApplied ? "primary.main" : "transparent",
                  color: isFilterApplied ? "#fff" : "text.secondary",
                  position: "relative",
                  width: 32,
                  height: 32,
                  transition: "0.3s ease-in-out",
                  "&:hover": {
                    bgcolor: isFilterApplied ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <FilterListRoundedIcon fontSize="small" />

                {/* Active dot */}
                {isFilterApplied && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {hasPageAccess("access_matter_templates_create") && (
            <Tooltip
              title={
                isLimitReached("current_case_template_limit")
                  ? "You've reached your Matter Template limit. Upgrade your plan to create more Templates."
                  : isLimitReached("current_custom_fields_count")
                    ? "You've reached your Custom Field limit. Upgrade your plan to add more fields."
                    : ""
              }
              disableHoverListener={
                !isLimitReached("current_case_template_limit") &&
                !isLimitReached("current_custom_fields_count")
              }
            >
              <span>
                <Button
                  id="access_matter_templates_create"
                  size="small"
                  variant="contained"
                  sx={{
                    textTransform: "capitalize",
                    cursor:
                      isLimitReached("current_case_template_limit") ||
                      isLimitReached("current_custom_fields_count")
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      isLimitReached("current_case_template_limit") ||
                      isLimitReached("current_custom_fields_count")
                        ? 0.8
                        : 1,
                  }}
                  disabled={
                    isLimitReached("current_case_template_limit") ||
                    isLimitReached("current_custom_fields_count")
                  }
                  onClick={() => {
                    if (
                      !isLimitReached("current_case_template_limit") &&
                      !isLimitReached("current_custom_fields_count")
                    ) {
                      showDialog(
                        <CreatecaseTemplateForm templates={allCaseTemplate} />,
                        {
                          dialogTitle: "Matter Template",
                          fullScreen: true,
                          width: "100%",
                          formType: "matter",
                          dialogId: "create-matter-template",
                        },
                      );
                    }
                  }}
                  startIcon={<AddRounded />}
                  endIcon={
                    isLimitReached("current_case_template_limit") ||
                    isLimitReached("current_custom_fields_count") ? (
                      <LockRounded
                        sx={{
                          fontSize: "1rem",
                          color: "error.main",
                        }}
                      />
                    ) : null
                  }
                >
                  {isLimitReached("current_case_template_limit") ||
                  isLimitReached("current_custom_fields_count")
                    ? "Limit Reached"
                    : "Create Template"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          ...pageScrollStyle,
          overflow: "auto",
          flex: 1,
          pb: 2,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "1rem",
            }}
          >
            {[...Array(6)].map((_, index) => (
              <TemplateCardSkeleton key={index} />
            ))}
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error">
              Error loading templates: {error}
            </Typography>
          </Box>
        ) : allCaseTemplate?.length === 0 && true ? (
          <NoTemplateFound />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "1rem",
            }}
          >
            {allCaseTemplate?.map((template) => (
              <Box
                key={template.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative",
                    borderRadius: "0.625rem",
                    border: "1px solid #DADADA",
                    overflow: "hidden",
                    "&:hover ": {
                      boxShadow: "0rem 0rem 0.5rem #0000001a",
                    },
                    "&:hover .action-box": {
                      bottom: "0rem",
                      transform: "translateY(0)",
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "0.4rem",
                      p: "0.5rem",
                      bgcolor: "#F5F5F5",
                    }}
                  >
                    <Tooltip title={template.template_name}>
                      <Typography
                        variant="body1"
                        sx={{
                          textTransform: "capitalize",
                          fontSize: "1rem",
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                        }}
                      >
                        {template.template_name}
                      </Typography>
                    </Tooltip>
                    {template?.is_standard && (
                      <Chip
                        label="Standard"
                        size="small"
                        color="primary"
                        sx={{
                          fontSize: "0.7rem",
                          height: "1.4rem",
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      textAlign: "left",
                      p: "1rem",
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                    }}
                  >
                    {template?.template_description ? (
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#606060",
                            fontSize: "0.9rem",
                            fontWeight: "400",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: expandedDesc?.[template.id]
                              ? "unset"
                              : 3,
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "normal",
                            height:
                              template.template_description.length > 120
                                ? "4rem"
                                : "auto",
                            overflowY: expandedDesc?.[template.id]
                              ? "auto"
                              : "hidden",
                            wordBreak: "break-word",
                            ...pageScrollStyle,
                          }}
                        >
                          {template.template_description}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "1rem",
                          }}
                        >
                          {!template?.is_standard && (
                            <Button
                              variant="text"
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                textTransform: "none",
                              }}
                              onClick={() => handleEditDescription(template)}
                            >
                              <i>
                                <BorderColorRoundedIcon
                                  sx={{ fontSize: "0.7rem" }}
                                />
                                Edit Note
                              </i>
                            </Button>
                          )}
                          {template?.template_description.length > 120 && (
                            <Button
                              variant="text"
                              size="small"
                              sx={{
                                p: 0,
                                minWidth: 0,
                                fontSize: "0.7rem",
                                textTransform: "none",
                              }}
                              onClick={() =>
                                setExpandedDesc((prev) => ({
                                  ...prev,
                                  [template.id]: !prev?.[template.id],
                                }))
                              }
                            >
                              <i>
                                {expandedDesc?.[template.id]
                                  ? "Read less"
                                  : "Read more"}
                              </i>
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: "#606060",
                          fontSize: "0.9rem",
                          fontWeight: "400",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        No description available{" "}
                        <Button
                          variant="text"
                          size="small"
                          sx={{ p: 0, fontSize: "0.7rem" }}
                          onClick={() => handleAddNote(template)}
                        >
                          <i>
                            <AddRoundedIcon sx={{ fontSize: "0.7rem" }} /> Add
                            Note
                          </i>
                        </Button>
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <ScheduleRoundedIcon
                        sx={{ fontSize: "1rem", color: "#606060" }}
                      />
                      <Typography
                        sx={{
                          color: "#606060",
                          fontSize: "0.8rem",
                          fontWeight: "500",
                        }}
                      >
                        Update By:{" "}
                        {template.updated_by
                          ? typeof template.updated_by === "object"
                            ? template.updated_by.first_name ||
                              template.updated_by.email ||
                              "Unknown"
                            : template.updated_by
                          : template.created_by
                            ? typeof template.created_by === "object"
                              ? template.created_by.first_name ||
                                template.created_by.email ||
                                "Unknown"
                              : template.created_by
                            : "System"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid #DADADA",
                      height: "2rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      px: "0.5rem",
                      color: template.practice_area?.name ?? "error",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#606060",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Practice Area:
                    </Typography>
                    <Chip
                      label={template.practice_area?.name || "General"}
                      size="small"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        height: "1.4rem",
                      }}
                    />
                  </Box>
                  <Box
                    className="action-box"
                    sx={{
                      position: "absolute",
                      bottom: "-2rem",
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      background: "#FFFFFF40",
                      boxShadow: "0 0.5rem 1rem 0 #1F268780",
                      backdropFilter: "blur(4px)",
                      WebkitBackdropFilter: "blur(4px)",
                      borderRadius: "0.8rem",
                      border: "1px solid #FFFFFF2E",
                      transform: "translateY(1rem)",
                      opacity: 0,
                      transition: "all 0.3s ease",
                      px: "0.5rem",
                      py: "0.2rem",
                      gap: "0.5rem",
                    }}
                  >
                    <>
                      {template.is_standard && isDevMode && (
                        <>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() =>
                              showDialog(
                                <CreatecaseTemplateForm
                                  editTempData={template}
                                />,
                                {
                                  dialogTitle: "Edit Matter Template",
                                  fullScreen: true,
                                  width: "100%",
                                  formType: "matter",
                                  dialogId: "create-matter-template",
                                },
                              )
                            }
                          >
                            open template
                          </Button>

                          <Button
                            variant="text"
                            size="small"
                            color="primary"
                            onClick={() =>
                              showDialog(
                                <MatterForm
                                  formData={template}
                                  onClose={() => {}}
                                  onSubmitSuccess={() => {}}
                                />,
                                {
                                  dialogTitle: "Preview Form",
                                  fullScreen: true,
                                  width: "100%",
                                  formType: "matter",
                                },
                              )
                            }
                          >
                            open matter form
                          </Button>
                        </>
                      )}

                      {hasPageAccess("access_matter_templates_edit") && (
                        <Button
                          id="access_matter_templates_edit"
                          variant="contained"
                          size="small"
                          endIcon={<BorderColorRounded fontSize="0.8rem" />}
                          onClick={() => handleEditTemplate(template)}
                          sx={{ textTransform: "capitalize" }}
                        >
                          Edit
                        </Button>
                      )}
                    </>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination Section - Fixed at Bottom */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Pagination
          page={page}
          count={count ? Math.ceil(count / pageSize) : 0}
          variant="outlined"
          color="primary"
          size="small"
          onChange={handlePageChange}
        />
      </Box>

      <FilterDrawer
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        anchorEl={filterAnchor}
        isFilterApplied={isFilterApplied}
        title="Task Filters"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        No filter register there{" "}
      </FilterDrawer>
    </Box>
  );
};

export default CaseTemplate;
